import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  ActivityUserAssignment,
  CreateActivityUserAssignmentDto,
  UpdateActivityUserAssignmentDto,
  ActivityTeachingAssignment,
  CreateActivityTeachingAssignmentDto,
  UpdateActivityTeachingAssignmentDto,
  ActivityLessonLink,
  BulkUsersAssignmentDto,
  BulkTeachingAssignmentsDto,
} from '@sphere/learning/domain'

export const assignmentsClient = {
  activityUserAssignments: {
    getByActivity: async (activityId: string) => {
      const { data } = await HttpClient.get<ActivityUserAssignment[]>(
        `/learning/activity-assignments/user/by-activity/${activityId}`
      )
      return data
    },
    getByUser: async (userId: string) => {
      const { data } = await HttpClient.get<ActivityUserAssignment[]>(
        `/learning/activity-assignments/user/by-user/${userId}`
      )
      return data
    },
    create: async (data: CreateActivityUserAssignmentDto) => {
      const { data: result } = await HttpClient.post<ActivityUserAssignment>(
        '/learning/activity-assignments/user',
        data
      )
      return result
    },
    bulkCreate: async (data: BulkUsersAssignmentDto) => {
      const { data: result } = await HttpClient.post<ActivityUserAssignment[]>(
        '/learning/activity-assignments/user/bulk',
        data
      )
      return result
    },
    update: async (id: string, data: UpdateActivityUserAssignmentDto) => {
      const { data: result } = await HttpClient.patch<ActivityUserAssignment>(
        `/learning/activity-assignments/user/${id}`,
        data
      )
      return result
    },
    delete: async (id: string) => {
      await HttpClient.delete<void>(`/learning/activity-assignments/user/${id}`)
    },
  },
  activityTeachingAssignments: {
    getByActivity: async (activityId: string) => {
      const { data } = await HttpClient.get<ActivityTeachingAssignment[]>(
        `/learning/activity-assignments/teaching/by-activity/${activityId}`
      )
      return data
    },
    getByTeaching: async (teachingId: string) => {
      const { data } = await HttpClient.get<ActivityTeachingAssignment[]>(
        `/learning/activity-assignments/teaching/by-teaching/${teachingId}`
      )
      return data
    },
    create: async (data: CreateActivityTeachingAssignmentDto) => {
      const { data: result } =
        await HttpClient.post<ActivityTeachingAssignment>(
          '/learning/activity-assignments/teaching',
          data
        )
      return result
    },
    bulkCreate: async (data: BulkTeachingAssignmentsDto) => {
      const { data: result } = await HttpClient.post<
        ActivityTeachingAssignment[]
      >('/learning/activity-assignments/teaching/bulk', data)
      return result
    },

    update: async (id: string, data: UpdateActivityTeachingAssignmentDto) => {
      const { data: result } =
        await HttpClient.patch<ActivityTeachingAssignment>(
          `/learning/activity-assignments/teaching/${id}`,
          data
        )
      return result
    },

    delete: async (id: string) => {
      await HttpClient.delete<void>(
        `/learning/activity-assignments/teaching/${id}`
      )
    },
  },
  activityLessonLinks: {
    // Lister les activités d'une leçon (ordonnées)
    getByLesson: async (lessonId: string) => {
      const { data } = await HttpClient.get<ActivityLessonLink[]>(
        `/learning/activity-lesson-links/lesson/${lessonId}`
      )
      return data
    },

    getByTeaching: async (teachingId: string) => {
      const { data } = await HttpClient.get<ActivityLessonLink[]>(
        `/learning/activity-lesson-links/teaching/${teachingId}`
      )
      return data
    },

    // Lister les leçons d'une activité
    getByActivity: (activityId: string) =>
      HttpClient.get<ActivityLessonLink[]>(
        `/learning/activity-lesson-links/activity/${activityId}`
      ),

    // Créer un lien activité-leçon
    create: (data: {
      activityId: string
      lessonId: string
      addedByUserId: string
      orderInLesson?: number
    }) =>
      HttpClient.post<ActivityLessonLink>(
        '/learning/activity-lesson-links',
        data
      ),

    // Supprimer un lien
    remove: (activityId: string, lessonId: string) =>
      HttpClient.delete<void>(
        `/learning/activity-lesson-links/${activityId}/${lessonId}`
      ),

    // Réordonner les activités d'une leçon
    reorder: (lessonId: string, activityIds: string[]) =>
      HttpClient.post<void>(
        `/learning/activity-lesson-links/lesson/${lessonId}/reorder`,
        { activityIds }
      ),
  },
}
